import { Venue } from '../types';

export type SortOption = 'recommended' | 'rating' | 'price-asc' | 'price-desc';
export type PriceFilter = 'all' | 'budget' | 'mid-range' | 'premium' | 'luxury';
export type AmenityFilter = 'private' | 'membership' | 'accessible' | 'parking';

export const PRICE_FILTER_LABELS: Record<Exclude<PriceFilter, 'all'>, string> = {
  budget: 'Budget',
  'mid-range': 'Mid-range',
  premium: 'Premium',
  luxury: 'Luxury',
};

export const SORT_OPTION_LABELS: Record<SortOption, string> = {
  recommended: 'Recommended',
  rating: 'Highest rated',
  'price-asc': 'Price: low',
  'price-desc': 'Price: high',
};

export const AMENITY_FILTER_CONFIG: Array<{ key: AmenityFilter; label: string }> = [
  { key: 'private', label: 'Private' },
  { key: 'membership', label: 'Members' },
  { key: 'accessible', label: 'Access' },
  { key: 'parking', label: 'Parking' },
];

export const VALID_PRICE_FILTERS = new Set<PriceFilter>(['all', 'budget', 'mid-range', 'premium', 'luxury']);
export const VALID_AMENITY_FILTERS = new Set<AmenityFilter>(['private', 'membership', 'accessible', 'parking']);
export const NEARBY_RADIUS_OPTIONS = [5, 10, 25] as const;

export const poundCount = (value: string) => (value?.match(/£/g) || []).length || 999;

export function getPriceBand(priceRange?: string | null): Exclude<PriceFilter, 'all'> | null {
  const pounds = poundCount(priceRange ?? '');
  if (pounds === 1) return 'budget';
  if (pounds === 2) return 'mid-range';
  if (pounds === 3) return 'premium';
  if (pounds >= 4 && pounds < 999) return 'luxury';
  return null;
}

export function matchesAmenityFilter(venue: Venue, filter: AmenityFilter): boolean {
  switch (filter) {
    case 'private':
      return Boolean(venue.privateSessionsAvailable);
    case 'membership':
      return Boolean(venue.membershipAvailable);
    case 'accessible':
      return Boolean(venue.wheelchairAccessible);
    case 'parking':
      return Boolean(venue.parkingAvailable);
    default:
      return false;
  }
}

export function toggleAmenityFilter(filters: AmenityFilter[], key: AmenityFilter): AmenityFilter[] {
  const current = new Set(filters);
  if (current.has(key)) current.delete(key);
  else current.add(key);

  return AMENITY_FILTER_CONFIG.map((item) => item.key).filter((value) => current.has(value));
}

const DEPRIORITIZED_LEAD_VENUE_NAMES = new Set(['go health woking']);
const DEPRIORITIZED_LEAD_VENUE_SLUGS = new Set(['go-health-woking', 'go-health-club-woking', 'go-health-woking-club']);

export function isDeprioritizedLeadVenue(venue: Pick<Venue, 'name' | 'slug'>) {
  const normalizedName = venue.name.trim().toLowerCase();
  const normalizedSlug = venue.slug.trim().toLowerCase();
  return DEPRIORITIZED_LEAD_VENUE_NAMES.has(normalizedName) || DEPRIORITIZED_LEAD_VENUE_SLUGS.has(normalizedSlug);
}
